import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Link, useHistory, useParams } from "react-router-dom";
import { PageHeader, Button, notification } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useInjectReducer, useInjectSaga } from "redux-injectors";
import { yupResolver } from "@hookform/resolvers/yup";
import { sliceKey, reducer, actions } from "./slice";
import { companyEditSaga } from "./saga";
import * as selectors from "./selectors";
import AddCompanyForm from "../../../../forms/AddCompanyForm";
import routes from "../../../../../routes";
import getSchema from "../../../../schemas/addCompanySchema";
import Loader from "../../../../components/Loader";

const EditCompany = () => {
  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({ key: sliceKey, saga: companyEditSaga });
  const dispatch = useDispatch();
  const history = useHistory();
  const { companyId } = useParams();
  // effects
  useEffect(() => {
    dispatch(actions.fetchCompanyDetails({ id: companyId }));
    dispatch(actions.fetchDropdown(["businessType", "paymentMethod"]));
    dispatch(actions.fetchCountries());
    return () => {
      dispatch(actions.initializeState());
    };
  }, [dispatch, companyId]);

  // selectors
  const dropdowns = useSelector(selectors.selectDropdowns);
  const countries = useSelector(selectors.selectCountries);
  const states = useSelector(selectors.selectStates);
  const cities = useSelector(selectors.selectCities);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const companyDetails = useSelector(selectors.selectCompanyDetails) || {};
  const error = useSelector(selectors.selectError);
  const loading = useSelector(selectors.selectLoading);

  useEffect(() => {
    if (error && error !== "") {
      notification.error({
        message: "Error Updating Company",
        description: error,
      });
      setTimeout(() => {
        dispatch(actions.initializeError());
      }, 1500);
    }
  }, [error, dispatch]);

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors },
    trigger,
  } = useForm({
    defaultValues: {
      companyName: "",
      businessType: "",
      cin: "",
      gst: "",
      pan: "",
      abbreviation: "",
      registeredAddress: {
        addressLine1: "",
        addressLine2: "",
        district: "",
        city: "",
        state: "",
        country: "",
        pincode: "",
        landmark: "",
      },
      /*billingAddress: [
        {
          addressLine1: "",
          addressLine2: "",
          district: "",
          city: "",
          state: "",
          country: "",
          pincode: "",
          landmark: "",
        },
      ],
      pickupAddress: [
        {
          addressLine1: "",
          addressLine2: "",
          district: "",
          city: "",
          state: "",
          country: "",
          pincode: "",
          landmark: "",
        },
      ],*/
      priceVisible: true,
      allowUserPickupAddress: false,
      paymentMethodsAvailable: [],
    },
    resolver: yupResolver(getSchema(true)),
  });

  const selectedCountry = watch("registeredAddress.country");
  const selectedState = watch("registeredAddress.state");

  useEffect(() => {
    if (selectedState) {
      // no operation for reset
    } else {
      reset(companyDetails);
    }
  }, [reset, companyDetails, selectedState]);

  useEffect(() => {
    if (selectedCountry)
      dispatch(actions.fetchStates({ countryId: selectedCountry }));
  }, [selectedCountry, dispatch]);

  useEffect(() => {
    if (selectedState) {
      dispatch(actions.fetchCities({ stateId: selectedState }));
    }
  }, [selectedState, dispatch]);

  const handleSuccess = () => {
    notification.success({
      message: "Company got edited successfully",
    });
    history.push(routes.backOffice.companies.list);
  };

  const handleFailure = () => {
    console.log("An error occured");
  };

  const onSubmit = (data) => {
    const payloadData = {
      companyName: data.companyName,
      businessType: data.businessType,
      cin: data.cin,
      gst: data.gst,
      pan: data.pan,
      registeredAddress: data.registeredAddress,
      abbreviation: data.abbreviation,
      //billingAddress: data.billingAddress,
      //pickupAddress: data.pickupAddress,
      priceVisible: data.priceVisible,
      allowUserPickupAddress: data.allowUserPickupAddress,
      paymentMethodsAvailable: data.paymentMethodsAvailable,
    };
    const payload = {
      id: companyId,
      onSuccess: handleSuccess,
      data: payloadData,
      onFailure: handleFailure,
    };

    dispatch(actions.updateCompany(payload));
  };

  return (
    <div style={{ height: "100%" }}>
      <PageHeader
        ghost={false}
        title="Edit Company Account"
        subTitle={companyDetails.companyName}
        extra={[
          <Button key="1">
            <Link to={routes.backOffice.companies.list}>Cancel</Link>
          </Button>,
          <Button
            disabled={loading}
            loading={loading}
            key="2"
            type="primary"
            onClick={handleSubmit(onSubmit)}
          >
            Update
          </Button>,
        ]}
      />
      <div
        style={{
          height: "calc(100vh - 137px)",
          overflowY: "auto",
          padding: 24,
        }}
      >
        {loading ? <Loader /> : ""}
        {
          <AddCompanyForm
            isEdit={true}
            options={{
              businessType: dropdowns.businessType,
              states,
              cities,
              countries,
              paymentMethods: dropdowns.paymentMethod,
            }}
            control={control}
            setValue={setValue}
            watch={watch}
            errors={errors}
            trigger={trigger}
          />
        }
      </div>
    </div>
  );
};

export default EditCompany;
